import type {
  WorklogSummaryItem,
  PlannedVsActualItemRow,
  PlannedVsActualBoardGroup,
  PlannedVsActualBoardRow,
} from '../api/report.api';
import { formatHours as _formatHoursBase } from './timesheet-capacity';

// ─── Formatting helpers (shared by table components) ─────────────────────────

/** Format hours for planned-vs-actual tables (decimal with "h" suffix) */
export function formatHours(h: number): string {
  const base = _formatHoursBase(h);
  return base === '-' ? '-' : `${base}h`;
}

export function formatPercent(p: number): string {
  return p.toFixed(2);
}

/**
 * Determine the CSS class for deviation coloring.
 *
 * Semantics:
 * - Red (over)    → Time Spent exceeds Original Estimate
 * - Green (ok)    → Time Spent is close to or within estimate (deviation >= -20%)
 * - Yellow (warn) → Significantly under-logged (deviation < -20% of estimate)
 * - Grey (zero)   → No estimate and no time spent
 */
export function deviationClass(
  deviation: number,
  originalEstimate: number,
  timeSpent: number,
): string {
  // No data at all
  if (originalEstimate === 0 && timeSpent === 0) return 'rp-pva-table__deviation--zero';
  // Over budget
  if (deviation > 0) return 'rp-pva-table__deviation--over';
  // Has estimate — check how much has been logged
  if (originalEstimate > 0) {
    const completionRatio = timeSpent / originalEstimate;
    // On track: logged at least 80% of estimate (deviation >= -20%)
    if (completionRatio >= 0.8) return 'rp-pva-table__deviation--ok';
    // Under-logged: significant gap between estimate and actual
    return 'rp-pva-table__deviation--warn';
  }
  // No estimate but has time spent (unplanned work)
  if (timeSpent > 0) return 'rp-pva-table__deviation--over';
  return 'rp-pva-table__deviation--zero';
}
import type { MondayItem, MondayItemColumnValue } from '../api/monday.api';
import type { FieldMapping } from '../api/board-config.api';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseTimeline(cv: MondayItemColumnValue): { from: string; to: string } | null {
  if (!cv.value) return null;
  try {
    const parsed = JSON.parse(cv.value) as { from?: string; to?: string };
    if (parsed.from && parsed.to) return { from: parsed.from, to: parsed.to };
  } catch { /* ignore */ }
  return null;
}

function parseAssigneeNames(cv: MondayItemColumnValue): string {
  return cv.text || '';
}

function parseHours(cv: MondayItemColumnValue): number {
  if (!cv.value && !cv.text) return 0;
  const text = cv.text || '';
  const num = parseFloat(text);
  return isNaN(num) ? 0 : num;
}

/**
 * Count working days (Mon-Fri) between two dates inclusive.
 */
export function countWorkingDays(from: string, to: string): number {
  const start = new Date(from + 'T00:00:00');
  const end = new Date(to + 'T00:00:00');
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  let count = 0;
  const current = new Date(start);
  while (current <= end) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) count++;
    current.setDate(current.getDate() + 1);
  }
  return count;
}

// ─── Item extraction ─────────────────────────────────────────────────────────

export interface ExtractedItem {
  itemId: string;
  boardId: string;
  boardName: string;
  itemName: string;
  assignees: string;
  startDate: string | null;
  endDate: string | null;
  originalEstimate: number;
  isSubitem: boolean;
}

/**
 * Extract report-relevant fields from a MondayItem using field mapping.
 */
export function extractItemData(
  item: MondayItem,
  fieldMapping: FieldMapping,
  boardId: string,
  boardName: string,
  isSubitem: boolean,
): ExtractedItem {
  let startDate: string | null = null;
  let endDate: string | null = null;
  let assignees = '';
  let hoursTotal = 0;
  let hoursPerDay = 0;

  for (const cv of item.column_values) {
    if (fieldMapping.timelineColumnId && cv.id === fieldMapping.timelineColumnId) {
      const timeline = parseTimeline(cv);
      if (timeline) {
        startDate = timeline.from;
        endDate = timeline.to;
      }
    }
    if (fieldMapping.assigneesColumnId && cv.id === fieldMapping.assigneesColumnId) {
      assignees = parseAssigneeNames(cv);
    }
    if (fieldMapping.scheduledHoursColumnId && cv.id === fieldMapping.scheduledHoursColumnId) {
      hoursTotal = parseHours(cv);
    }
    if (fieldMapping.scheduledHoursPerDayColumnId && cv.id === fieldMapping.scheduledHoursPerDayColumnId) {
      hoursPerDay = parseHours(cv);
    }
  }

  // Determine original estimate
  let originalEstimate = 0;
  if (hoursTotal > 0) {
    originalEstimate = hoursTotal;
  } else if (hoursPerDay > 0 && startDate && endDate) {
    originalEstimate = hoursPerDay * countWorkingDays(startDate, endDate);
  }

  return {
    itemId: item.id,
    boardId,
    boardName,
    itemName: item.name,
    assignees,
    startDate,
    endDate,
    originalEstimate,
    isSubitem,
  };
}

// ─── Core computation ────────────────────────────────────────────────────────

/**
 * Merge extracted items with worklog summaries and compute derived fields.
 */
export function computePlannedVsActual(
  extractedItems: ExtractedItem[],
  worklogSummaries: WorklogSummaryItem[],
): PlannedVsActualItemRow[] {
  const worklogMap = new Map<string, number>();
  for (const w of worklogSummaries) {
    worklogMap.set(w.mondayItemId, (worklogMap.get(w.mondayItemId) ?? 0) + w.totalHours);
  }

  return extractedItems.map((item) => {
    const timeSpent = worklogMap.get(item.itemId) ?? 0;
    const remainingEstimate = Math.max(0, item.originalEstimate - timeSpent);
    const deviation = timeSpent - item.originalEstimate;
    const deviationPercent = item.originalEstimate > 0
      ? (deviation / item.originalEstimate) * 100
      : 0;

    return {
      itemId: item.itemId,
      boardId: item.boardId,
      boardName: item.boardName,
      itemName: item.itemName,
      assignees: item.assignees,
      startDate: item.startDate,
      endDate: item.endDate,
      originalEstimate: item.originalEstimate,
      timeSpent,
      remainingEstimate,
      deviation,
      deviationPercent,
      isSubitem: item.isSubitem,
    };
  });
}

// ─── Board aggregation ───────────────────────────────────────────────────────

function computeSummary(items: PlannedVsActualItemRow[]) {
  const assigneeSet = new Set<string>();
  let originalEstimate = 0;
  let timeSpent = 0;

  for (const item of items) {
    originalEstimate += item.originalEstimate;
    timeSpent += item.timeSpent;
    if (item.assignees) {
      for (const name of item.assignees.split(',').map((n) => n.trim()).filter(Boolean)) {
        assigneeSet.add(name);
      }
    }
  }

  const remainingEstimate = Math.max(0, originalEstimate - timeSpent);
  const deviation = timeSpent - originalEstimate;
  const deviationPercent = originalEstimate > 0
    ? (deviation / originalEstimate) * 100
    : 0;

  return {
    totalItems: items.length,
    totalAssignees: assigneeSet.size,
    originalEstimate,
    timeSpent,
    remainingEstimate,
    deviation,
    deviationPercent,
  };
}

/**
 * Group items by board and compute per-board summaries.
 */
export function groupByBoard(
  items: PlannedVsActualItemRow[],
): PlannedVsActualBoardGroup[] {
  const boardMap = new Map<string, PlannedVsActualItemRow[]>();
  const boardNameMap = new Map<string, string>();

  for (const item of items) {
    if (!boardMap.has(item.boardId)) {
      boardMap.set(item.boardId, []);
      boardNameMap.set(item.boardId, item.boardName);
    }
    boardMap.get(item.boardId)!.push(item);
  }

  return Array.from(boardMap.entries()).map(([boardId, boardItems]) => ({
    boardId,
    boardName: boardNameMap.get(boardId) ?? boardId,
    items: boardItems,
    summary: computeSummary(boardItems),
  }));
}

/**
 * Aggregate items into board-level rows.
 */
export function aggregateByBoard(
  items: PlannedVsActualItemRow[],
): PlannedVsActualBoardRow[] {
  const groups = groupByBoard(items);
  return groups.map((g) => ({
    boardId: g.boardId,
    boardName: g.boardName,
    ...g.summary,
  }));
}

/**
 * Compute grand total across all items.
 */
export function computeGrandTotal(items: PlannedVsActualItemRow[]) {
  return computeSummary(items);
}